/** Cliente da API.
 *
 * O token de acesso é curto e vive em memória (não em localStorage: XSS levaria o token).
 * O refresh mora num cookie httpOnly que o navegador manda sozinho para /auth/*. Quando
 * uma chamada volta 401, o cliente tenta renovar UMA vez e repete a chamada; se o refresh
 * também falhar, avisa quem escuta e a aplicação volta para o login.
 */

import type {
  Account,
  Category,
  Family,
  HealthLevel,
  InstallmentPlan,
  Occurrence,
  Projection,
  ProjectionSummary,
  Recurrence,
  Scenario,
  Scope,
  Settings,
  Statement,
  Transaction,
  TransactionIn,
  User,
} from "./types";

const BASE = import.meta.env.VITE_API_URL ?? "/api";

let accessToken: string | null = null;
let onUnauthorized: (() => void) | null = null;
/** Cada login/refresh bem-sucedido avança a geração. Uma renovação que falha só apaga o
 *  token se ele ainda for o dela: sem isso, o `refresh()` do boot (que falha quando não há
 *  cookie) chegava atrasado e apagava o token de um login que já tinha dado certo. */
let geracao = 0;

export function setUnauthorizedHandler(fn: () => void) {
  onUnauthorized = fn;
}
export function getToken() {
  return accessToken;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

type Options = Omit<RequestInit, "body"> & { body?: unknown; query?: Record<string, unknown> };

function url(path: string, query?: Record<string, unknown>) {
  const u = new URL(BASE + path, window.location.origin);
  for (const [k, v] of Object.entries(query ?? {})) {
    if (v !== undefined && v !== null && v !== "") u.searchParams.set(k, String(v));
  }
  return u.toString();
}

async function parseError(res: Response): Promise<string> {
  try {
    const data = await res.json();
    const d = data?.detail;
    if (typeof d === "string") return d;
    // erro de validação do FastAPI: [{loc, msg, type}]
    if (Array.isArray(d)) return d.map((e) => e?.msg ?? String(e)).join("; ");
    return res.statusText || `HTTP ${res.status}`;
  } catch {
    return res.statusText || `HTTP ${res.status}`;
  }
}

async function raw<T>(path: string, opts: Options = {}, retry = true): Promise<T> {
  const { body, query, headers, ...rest } = opts;
  const res = await fetch(url(path, query), {
    ...rest,
    credentials: "include",
    headers: {
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...headers,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401 && retry && !path.startsWith("/auth/")) {
    const renewed = await refresh().catch(() => null);
    if (renewed) return raw<T>(path, opts, false);
    accessToken = null;
    onUnauthorized?.();
    throw new ApiError(401, "Sessão expirada. Entre de novo.");
  }
  if (!res.ok) throw new ApiError(res.status, await parseError(res));
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

const get = <T>(path: string, query?: Record<string, unknown>) => raw<T>(path, { query });
const post = <T>(path: string, body?: unknown, query?: Record<string, unknown>) =>
  raw<T>(path, { method: "POST", body, query });
const patch = <T>(path: string, body?: unknown, query?: Record<string, unknown>) =>
  raw<T>(path, { method: "PATCH", body, query });
const del = <T>(path: string, query?: Record<string, unknown>) =>
  raw<T>(path, { method: "DELETE", query });

// --- auth -------------------------------------------------------------------

interface Session {
  access_token: string;
  user: User;
}

export async function login(email: string, password: string): Promise<User> {
  const s = await raw<Session>("/auth/login", { method: "POST", body: { email, password } });
  accessToken = s.access_token;
  geracao += 1;
  return s.user;
}

export async function refresh(): Promise<User | null> {
  const minha = geracao;
  try {
    const s = await raw<Session>("/auth/refresh", { method: "POST" }, false);
    accessToken = s.access_token;
    geracao += 1;
    return s.user;
  } catch {
    // Só apaga se ninguém entrou nesse meio-tempo.
    if (geracao === minha) accessToken = null;
    return null;
  }
}

export async function logout(): Promise<void> {
  await raw("/auth/logout", { method: "POST" }).catch(() => {});
  accessToken = null;
}

export const me = () => get<User>("/auth/me");

/** Troca a senha. A resposta traz token novo: trocar a senha não derruba quem trocou. */
export async function changePassword(current_password: string, new_password: string): Promise<void> {
  const s = await raw<Session>("/auth/password", {
    method: "POST",
    body: { current_password, new_password },
  });
  accessToken = s.access_token;
}

// --- projeção ---------------------------------------------------------------

export const getProjection = (params: {
  scope?: string;
  frm?: string;
  to?: string;
  scenario_id?: string | null;
}) => get<Projection>("/projection", params);

export const getToday = (scope = "mine") => get<ProjectionSummary>("/today", { scope });

// --- contas -----------------------------------------------------------------

export const getAccounts = (include_archived = false) =>
  get<Account[]>("/accounts", { include_archived });
export const createAccount = (body: Partial<Account>) => post<Account>("/accounts", body);
export const updateAccount = (id: string, body: Partial<Account> & { archived?: boolean }) =>
  patch<Account>(`/accounts/${id}`, body);
export const deleteAccount = (id: string) => del<{ deleted: boolean }>(`/accounts/${id}`);
export const getAccountStatements = (id: string) =>
  get<Statement[]>(`/accounts/${id}/statements`);

// --- categorias -------------------------------------------------------------

export const getCategories = () => get<Category[]>("/categories");
export const createCategory = (body: { name: string; bucket: string }) =>
  post<Category>("/categories", body);
export const seedCategories = () => post<Category[]>("/categories/defaults");

// --- lançamentos ------------------------------------------------------------

export const getTransactions = (params: {
  frm?: string;
  to?: string;
  account_id?: string;
  status?: string;
  scenario_id?: string | null;
  limit?: number;
}) => get<Transaction[]>("/transactions", params);

export const createTransaction = (body: TransactionIn) =>
  post<Transaction & { installment_plan?: InstallmentPlan }>("/transactions", body);

export const updateTransaction = (id: string, body: Partial<TransactionIn>, scope: Scope = "single") =>
  patch<Transaction | { scope: Scope; updated: number; transactions: Transaction[] }>(
    `/transactions/${id}`,
    body,
    scope === "single" ? undefined : { scope },
  );

export const deleteTransaction = (id: string, scope: "single" | "from_here" = "single") =>
  del<{ deleted: boolean; skipped?: number | boolean }>(
    `/transactions/${id}`,
    scope === "single" ? undefined : { scope },
  );

export const confirmTransaction = (id: string, amount: number, paid = false) =>
  post<Transaction>(`/transactions/${id}/confirm`, { amount, paid });

export const clearTransaction = (id: string, paid_on?: string) =>
  post<Transaction>(`/transactions/${id}/clear`, undefined, { paid_on });

export const getUpcoming = (days = 30) =>
  get<Transaction[]>("/transactions/planned/upcoming", { days });

// --- planejado --------------------------------------------------------------

export const getRecurrences = () => get<Recurrence[]>("/recurrences");
export const createRecurrence = (body: Partial<Recurrence>) => post<Recurrence>("/recurrences", body);
export const previewRecurrence = (body: Partial<Recurrence>, count = 6) =>
  post<Occurrence[]>("/recurrences/preview", body, { count });
export const updateRecurrence = (id: string, body: Partial<Recurrence>) =>
  patch<Recurrence>(`/recurrences/${id}`, body);
export const deleteRecurrence = (id: string) => del<{ deleted: boolean }>(`/recurrences/${id}`);

export const getPlans = () => get<InstallmentPlan[]>("/installment-plans");
export const createPlan = (body: Partial<InstallmentPlan>) =>
  post<InstallmentPlan>("/installment-plans", body);
export const previewPlan = (body: Partial<InstallmentPlan>) =>
  post<{ no: number; due_on: string; amount: number }[]>("/installment-plans/preview", body);
export const updatePlan = (id: string, body: Partial<InstallmentPlan>) =>
  patch<InstallmentPlan>(`/installment-plans/${id}`, body);
export const deletePlan = (id: string) => del<{ deleted: boolean }>(`/installment-plans/${id}`);

export const getAccount = (id: string) => get<Account>(`/accounts/${id}`);
export const updateCategory = (id: string, body: Partial<Category>) =>
  patch<Category>(`/categories/${id}`, body);
export const deleteCategory = (id: string) => del<{ deleted: boolean }>(`/categories/${id}`);
export const removeMember = (familyId: string, userId: string) =>
  del<{ removed: boolean }>(`/families/${familyId}/members/${userId}`);

export const getHolidays = (frm: number, to: number) => get<string[]>("/holidays", { frm, to });

// --- faturas ----------------------------------------------------------------

export const getStatements = (status?: string) => get<Statement[]>("/statements", { status });
/** A fatura com os lançamentos dentro: quem sabe o que está numa fatura é ela mesma,
 *  pelo `card_statement_id`, e não uma janela de datas. */
export const getStatement = (id: string) =>
  get<Statement & { transactions: Transaction[] }>(`/statements/${id}`);
export const payStatement = (
  id: string,
  body: { amount?: number; paid_on?: string; from_account_id?: string },
) => post<Transaction>(`/statements/${id}/pay`, body);

// --- ajustes, família, cenários --------------------------------------------

export const getSettings = () =>
  get<{ name: string; avatar_color: string; timezone: string; settings: Settings }>("/settings");
export const updateSettings = (
  body: Partial<Settings> & { name?: string; avatar_color?: string; health_levels?: HealthLevel[] },
) => patch<{ settings: Settings }>("/settings", body);

export const getFamilies = () => get<Family[]>("/families");
export const createFamily = (name: string) => post<Family>("/families", { name });
export const addMember = (familyId: string, email: string, role = "member") =>
  post<{ user_id: string; name: string }>(`/families/${familyId}/members`, { email, role });
export const shareAccount = (familyId: string, account_id: string, visibility = "full") =>
  post<unknown>(`/families/${familyId}/shares`, { account_id, visibility });
export const unshareAccount = (familyId: string, accountId: string) =>
  del<unknown>(`/families/${familyId}/shares/${accountId}`);

export const getScenarios = () => get<Scenario[]>("/scenarios");
export const createScenario = (name: string) => post<Scenario>("/scenarios", { name });
export const applyScenario = (id: string) =>
  post<Scenario & { applied_transactions: number }>(`/scenarios/${id}/apply`);
export const deleteScenario = (id: string) => del<{ deleted: boolean }>(`/scenarios/${id}`);

export const health = () => get<{ ok: boolean; version: string }>("/health");
